import { apiClient } from '../apiClient';

/**
 * Helper to extract company slug from current URL path
 */
const getCompanySlug = (): string => {
  if (typeof window === 'undefined') return '';
  const parts = window.location.pathname.split('/').filter(Boolean);
  return parts[0] || '';
};

/**
 * Build RPC endpoint URL
 */
const buildUrl = (path: string): string => {
  const company = getCompanySlug();
  return `/api/${company}/rpc/settings${path}`;
};

/**
 * Simple in-memory cache for pages usage (to prevent repeated slow API calls)
 * Cache TTL: 30 seconds
 */
let pagesUsageCache: {
  data: any;
  timestamp: number;
  company: string;
} | null = null;
const PAGES_USAGE_CACHE_TTL = 30000; // 30 seconds

/**
 * Get cached pages usage if available and not expired
 */
function getCachedPagesUsage(company: string): any | null {
  if (!pagesUsageCache || pagesUsageCache.company !== company) {
    return null;
  }
  const age = Date.now() - pagesUsageCache.timestamp;
  if (age < PAGES_USAGE_CACHE_TTL) {
    return pagesUsageCache.data;
  }
  return null;
}

/**
 * Set cached pages usage
 */
function setCachedPagesUsage(company: string, data: any): void {
  pagesUsageCache = {
    data,
    timestamp: Date.now(),
    company,
  };
}

/**
 * RPC client for settings-related operations
 * All endpoints return { ok: true, data } or { ok: false, error }
 */
const rpcSettings = {
  /**
   * Get SMS limit and usage
   */
  async getSmsLimit() {
    const { data } = await apiClient.post(buildUrl('/get-sms-limit'), {});
    return data?.ok ? data.data : data;
  },

  /**
   * Get company enabled features
   */
  getCompanyFeatures() {
    return apiClient.post(buildUrl('/get-company-features'), {});
  },

  // ==================== ADMIN-SPECIFIC METHODS ====================

  /**
   * Get admin overview
   */
  getAdminOverview() {
    return apiClient.post(buildUrl('/admin/overview'), {});
  },

  /**
   * Get recent activity
   */
  getRecentActivity() {
    return apiClient.post(buildUrl('/admin/recent-activity'), {});
  },

  /**
   * Get SMS limit (admin scope)
   */
  getAdminSmsLimit() {
    return apiClient.post(buildUrl('/admin/sms-limit'), {});
  },

  /**
   * Get features (admin scope)
   */
  getAdminFeatures() {
    return apiClient.post(buildUrl('/admin/features'), {});
  },

  /**
   * Get failed logins
   */
  getFailedLogins(payload?: {
    page?: number;
    limit?: number;
    userId?: string;
    email?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    ipAddress?: string;
    device?: string;
    minAttempts?: number;
  }) {
    return apiClient.post(buildUrl('/admin/failed-logins'), payload || {});
  },

  /**
   * Unlock a blocked user account
   */
  unlockAccount(payload: { userId: string }) {
    return apiClient.post(buildUrl('/admin/unlock-account'), payload);
  },

  /**
   * Get leads count
   */
  getLeadsCount() {
    return apiClient.post(buildUrl('/admin/leads/count'), {});
  },

  /**
   * Get sender configuration
   */
  getSender() {
    return apiClient.post(buildUrl('/get-sender'), {});
  },

  /**
   * Create or update sender
   */
  createSender(payload: { fromNumber: string; displayName?: string; twilioPhoneSid?: string }) {
    return apiClient.post(buildUrl('/create-sender'), payload);
  },

  /**
   * Delete (deactivate) sender
   */
  deleteSender() {
    return apiClient.post(buildUrl('/delete-sender'), {});
  },

  /**
   * Get bulk templates
   */
  getBulkTemplates(kind: 'sms' | 'email') {
    return apiClient.post(buildUrl('/get-bulk-templates'), { kind });
  },

  /**
   * Get global custom fields
   */
  getCustomFields() {
    return apiClient.post(buildUrl('/admin/custom-fields/get'), {});
  },

  /**
   * Create global custom field
   */
  createCustomField(payload: { label: string; type: string; secret?: boolean; order?: number }) {
    return apiClient.post(buildUrl('/admin/custom-fields/create'), payload);
  },

  /**
   * Update global custom field
   */
  updateCustomField(fieldId: string, payload: { label?: string; type?: string; secret?: boolean; order?: number }) {
    return apiClient.post(buildUrl('/admin/custom-fields/update'), { fieldId, ...payload });
  },

  /**
   * Delete global custom field
   */
  deleteCustomField(fieldId: string) {
    return apiClient.post(buildUrl('/admin/custom-fields/delete'), { fieldId });
  },

  /**
   * Get storage usage (percentage only for company admin)
   * Note: storageState is automatically extracted by API interceptor for real-time updates
   */
  async getStorageUsage() {
    const response = await apiClient.post(buildUrl('/admin/storage-usage'), {});
    // storageState is automatically handled by interceptor
    // Return both data and storageState for direct access if needed
    return {
      ...(response.data?.ok ? response.data.data : response.data),
      storageState: response.data?.storageState
    };
  },

  /**
   * Recalculate storage usage from Cloudinary
   */
  async recalculateStorageUsage() {
    const { data } = await apiClient.post(buildUrl('/admin/storage-usage/recalculate'), {});
    return data?.ok ? data.data : data;
  },

  /**
   * Get user's pages usage
   * OPTIMIZED: Short timeout (5s) + caching to prevent blocking UI and repeated slow calls
   * Banner should load asynchronously without delaying page render
   */
  async getPagesUsage() {
    const company = getCompanySlug();
    
    // Check cache first
    const cached = getCachedPagesUsage(company);
    if (cached) {
      return cached;
    }
    
    try {
      const { data } = await apiClient.post(buildUrl('/user/pages-usage'), {}, {
        timeout: 15000, // 15 second timeout - increased to handle slow usage calculations
      });
      const result = data?.ok ? data.data : data;
      
      // Cache successful responses
      if (result) {
        setCachedPagesUsage(company, result);
      }
      
      return result;
    } catch (err) {
      // On error, return cached data if available (even if expired)
      const staleCache = pagesUsageCache && pagesUsageCache.company === company 
        ? pagesUsageCache.data 
        : null;
      if (staleCache) {
        return staleCache;
      }
      throw err;
    }
  },

  /**
   * Get company plan data (plan + overrides)
   * Returns company plan, limitsOverride, and storageLimitOverride
   */
  async getCompanyPlanData() {
    const { data } = await apiClient.post(buildUrl('/admin/company-plan-data'), {});
    return data?.ok ? data.data : data;
  },

  /**
   * Get company usage statistics (AI requests, automations, API calls)
   * Returns usage data for the current company
   */
  async getUsageStats() {
    const { data } = await apiClient.post(buildUrl('/admin/usage-stats'), {});
    return data?.ok ? data.data : data;
  },

  /**
   * Get company messaging settings (WhatsApp enablement only)
   */
  async getCompanyMessagingSettings() {
    const { data } = await apiClient.post(buildUrl('/get-company-messaging-settings'), {});
    return data?.ok ? data.data : data;
  },

  /**
   * Update company messaging settings (Admin only)
   * Updates enableSmsMessaging and/or enableWhatsAppMessaging boolean
   */
  async updateCompanyMessagingSettings(payload: {
    enableSmsMessaging?: boolean;
    enableWhatsAppMessaging?: boolean;
  }) {
    const { data } = await apiClient.post(buildUrl('/update-company-messaging-settings'), payload);
    return data?.ok ? data.data : data;
  },

  /**
   * Toggle WhatsApp messaging (immediate toggle, like login alert)
   */
  toggleWhatsAppMessaging(enabled: boolean) {
    return apiClient.post(buildUrl('/toggle-whatsapp-messaging'), { enabled }, {
      timeout: 15000, // 15 second timeout (same as other company settings operations)
    });
  },

  /**
   * Toggle SMS messaging (immediate toggle, like WhatsApp)
   */
  toggleSmsMessaging(enabled: boolean) {
    return apiClient.post(buildUrl('/toggle-sms-messaging'), { enabled }, {
      timeout: 15000, // 15 second timeout (same as other company settings operations)
    });
  },

  /**
   * Get audit history for top-ups and limit changes (Company Admin - without employee name)
   * POST /api/:company/rpc/settings/audit-topups
   */
  async getAuditTopups(payload: {
    page?: number;
    limit?: number;
  }) {
    const { data } = await apiClient.post(buildUrl('/audit-topups'), payload);
    return data?.ok ? data.data : data;
  },
};

export default rpcSettings;

